import {
  ABNORMAL_COLORS,
  type BrandSettings,
  collectedAtOf,
  computeReportSections,
  effectivePrint,
  formatDateTimeIST,
  LABVIEW_CONTACT_LINE,
  labviewFooterOn,
  parseRich,
  printsSignatures,
  type ReportContent,
  type ReportStatus,
  resolveAbnormal,
  sampleSummary,
} from '@quickreport/domain';
import { type PrintSection, type ReportData, toPrintSection, withDepartmentHeadings } from './data';

export interface SignatoryInfo {
  id: string;
  name: string;
  qualification: string;
  designation?: string | null;
  regNo?: string | null;
  /** Virtual path of the signature image passed to the renderer, if any. */
  signaturePath?: string;
}

export interface BuildReportInput {
  labNo: string;
  content: ReportContent;
  brand: BrandSettings;
  signatories: SignatoryInfo[];
  /** Virtual path of the logo image passed to the renderer, if any. */
  logoPath?: string;
  /** Virtual path of the full-page letterhead picture passed to the renderer, if any. */
  backgroundPath?: string;
  /** Present only once the report exists on the server; no code, no QR. */
  verify?: { code: string; qrPath: string };
  /** Sign-off time; becomes the PDF's creation date so the same report prints the same bytes. */
  signedAt: Date;
  /** Drives the watermark and the "Awaited" marks of partial reports. */
  status?: ReportStatus;
  /** Printed after the report number from the second version on, so old copies are recognisable. */
  version?: number;
}

/** A partial report prints "Awaited" where a result is still to come. */
function markAwaited(section: PrintSection): PrintSection {
  return {
    ...section,
    rows: section.rows.map((r) =>
      r.kind === 'test' && r.result === '' ? { ...r, result: 'Awaited' } : r,
    ),
  };
}

const WATERMARKS: Partial<Record<ReportStatus, string>> = {
  partial: 'PARTIAL',
  revoked: 'REVOKED',
  draft: 'DRAFT',
  awaiting_verification: 'DRAFT',
  awaiting_signature: 'DRAFT',
  amended: 'DRAFT',
};

/**
 * Everything the PDF layout needs, from a saved report and the lab's settings. Used by the
 * server (verification PDFs), the browser and the desktop app, so a report prints the same
 * everywhere.
 */
export function buildReportData(input: BuildReportInput): ReportData {
  const { content, brand } = input;
  const computed = computeReportSections(content);
  const v = content.visit;
  const p = content.patient;

  const sections: PrintSection[] = content.sections.map((s, i) => {
    const ref =
      s.performedBy?.kind === 'reference_lab' && s.performedBy.name.trim()
        ? s.performedBy
        : undefined;
    const doc =
      content.kind === 'generic' && content.title ? { ...s.doc, name: content.title } : s.doc;
    const printed = toPrintSection(doc, computed[i] as (typeof computed)[number], {
      abnormalStyle: abnormalPrintStyle(brand, content, i),
      ...(ref
        ? { performedAt: `${ref.name}${ref.accreditation ? `, ${ref.accreditation}` : ''}` }
        : {}),
    });
    return input.status === 'partial' ? markAwaited(printed) : printed;
  });

  const sexWord = p.sex === 'M' ? 'Male' : p.sex === 'F' ? 'Female' : p.sex === 'O' ? 'Other' : '';
  const name = [p.salutation ? `${p.salutation}.` : '', p.name].filter(Boolean).join(' ');
  const left: [string, string][] = [['Patient', name]];
  if (p.ageText || sexWord)
    left.push(['Age / Sex', [p.ageText, sexWord].filter(Boolean).join(' / ')]);
  if (p.externalId) left.push(['Patient ID', p.externalId]);
  if (v.referredBy) left.push(['Referred by', v.referredBy]);
  const sampleLine = content.samples.length ? sampleSummary(content.samples) : v.sampleType;
  if (sampleLine) left.push(['Sample', sampleLine]);
  if (p.address) left.push(['Address', p.address]);
  for (const f of content.fields) if (f.value.trim()) left.push([f.label, f.value]);

  const right: [string, string][] = [['Lab No.', input.labNo]];
  if (v.visitNo) right.push(['Visit No.', v.visitNo]);
  const collected = collectedAtOf(content);
  if (collected) right.push(['Collected', formatDateTimeIST(collected)]);
  if (v.receivedAt) right.push(['Received', formatDateTimeIST(v.receivedAt)]);
  right.push(['Reported', formatDateTimeIST(v.reportedAt ?? input.signedAt)]);

  const signedIds = new Set(content.sections.map((s) => s.signedBy).filter(Boolean));
  const signatories = input.signatories
    .filter((s) => signedIds.has(s.id))
    .map((s) => ({
      name: s.name,
      qualification: s.qualification,
      ...(s.designation ? { designation: s.designation } : {}),
      ...(s.regNo ? { regNo: s.regNo } : {}),
      ...(s.signaturePath ? { signature: s.signaturePath } : {}),
    }));

  const d = input.signedAt;
  return {
    meta: {
      title: content.title ?? sections[0]?.title ?? 'Laboratory report',
      reportId:
        input.version && input.version > 1 ? `${input.labNo} · v${input.version}` : input.labNo,
      patientName: name,
      date: {
        year: d.getUTCFullYear(),
        month: d.getUTCMonth() + 1,
        day: d.getUTCDate(),
        hour: d.getUTCHours(),
        minute: d.getUTCMinutes(),
      },
    },
    lab: {
      name: brand.labName || 'Your lab name',
      ...(brand.tagline ? { tagline: brand.tagline } : {}),
      ...(brand.nameAt && brand.nameAt !== 'right' ? { nameAt: brand.nameAt } : {}),
      address: brand.address,
      phones: brand.phones,
      ...(brand.email ? { email: brand.email } : {}),
      accent: brand.accent,
      ...(input.logoPath ? { logo: input.logoPath } : {}),
      ...(brand.disclaimer ? { disclaimer: brand.disclaimer } : {}),
      registrations: brand.registrations,
    },
    patient: { left, right },
    sections: withDepartmentHeadings(sections),
    signatories,
    ...(content.comments?.trim() ? { comments: parseRich(content.comments) } : {}),
    ...(input.verify
      ? { qr: { path: input.verify.qrPath, code: formatCode(input.verify.code) } }
      : {}),
    options: {
      letterhead: brand.letterhead,
      print: effectivePrint(brand),
      topMarginMm: brand.topMarginMm,
      bottomMarginMm: Math.min(
        140,
        brand.bottomMarginMm + signatureExtraMm(brand, signatories.length),
      ),
      signatureHeightMm: brand.signatureHeightMm,
      signatureWidthMm: brand.signatureWidthMm,
      signaturesOn: brand.signaturesOn,
      showEndOfReport: input.status !== 'partial',
      ...(input.backgroundPath ? { background: input.backgroundPath, endInFooter: true } : {}),
      ...(labviewFooterOn(brand) ? { labviewLine: LABVIEW_CONTACT_LINE } : {}),
      ...(input.status && WATERMARKS[input.status]
        ? { watermark: WATERMARKS[input.status] as string }
        : {}),
    },
  };
}

/**
 * The bottom margin was chosen for one row of signatures 9 mm tall. A taller signature, or a
 * second row (signatures go three across), needs that much more room so nothing runs into the
 * results.
 */
function signatureExtraMm(brand: BrandSettings, count: number): number {
  if (count === 0 || !printsSignatures(brand)) return 0;
  const rows = Math.ceil(count / 3);
  const h = brand.signatureHeightMm;
  return Math.max(0, h - 9) + (rows - 1) * (h + 16);
}

function formatCode(code: string): string {
  return code.length === 10 ? `${code.slice(0, 5)}-${code.slice(5)}` : code;
}

/**
 * How a result of section `i` prints when it is out of range, for the print layout. Nothing for the
 * usual bold red, so a report nobody has styled prints exactly as it always has.
 */
export function abnormalPrintStyle(
  brand: Pick<BrandSettings, 'abnormalBold' | 'abnormalColor'>,
  content: ReportContent,
  sectionIndex: number,
): (rowId: string) => { bold: boolean; color: string } | undefined {
  const rowStyles = content.sections[sectionIndex]?.rowStyles;
  return (rowId) => {
    const a = resolveAbnormal(brand, content.abnormal, rowStyles?.[rowId]);
    if (a.bold && a.color === 'red') return undefined;
    return { bold: a.bold, color: ABNORMAL_COLORS[a.color].hex };
  };
}
